import React, { useMemo, useState } from 'react';
import { Copy, Download, RotateCcw } from 'lucide-react';
import Button from './shared/Button';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import ToolkitLocalFileStep from './toolkit/ToolkitLocalFileStep';
import ToolkitCompareResults from './toolkit/ToolkitCompareResults';
import { downloadBlob } from '../utils/xlsx';
import { parsePepGuestXml, duplicateBillKeys, exportCleanPepXml, transactionRollup, parseGuestLedgerPdf, matchGlToPep } from '../utils/pepBalancing';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › PEP Guest Balancing — ported from the desktop's PEP Balancing › G – Guest Balancing.
// Checks a PEP guest file before it is imported: folio bills paid by HTS token, cards with the 2099 placeholder expiry,
// receipt ids repeated within a stay, and the transactions rolled up by accounting id — and, with the Opera
// naGuestLedgerDetail PDF, which guests are in one and not the other (last name + arrival + departure).
//
// Bills can be marked for removal (all duplicate receipts in one click) and the CLEANED file saved, ready to import.
// Both files are read in the browser and never uploaded — they carry guest names, e-mails and card tokens.

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const TABS = [['hts', 'HTS tokens'], ['fake', 'Fake expiry (2099)'], ['dups', 'Duplicate receipts'], ['txns', 'Transactions'], ['gl', 'Guest ledger (PDF)']];

function ToolkitPepGuestBalancingPage() {
    const toolkit = useToolkitProject();
    const [raw, setRaw] = useState(null);
    const [fileName, setFileName] = useState('');
    const [pep, setPep] = useState(null);
    const [gl, setGl] = useState(null);
    const [tab, setTab] = useState('hts');
    const [search, setSearch] = useState('');
    const [deleted, setDeleted] = useState(new Set());

    const stats = useMemo(() => {
        if (!pep) return null;
        const bills = pep.stays.flatMap((s) => s.bills);
        return {
            stays: pep.stays.length, bills: bills.length,
            hts: bills.filter((b) => b.isHTS).length, fake: bills.filter((b) => b.isFakeExp).length,
            dupStays: pep.stays.filter((s) => s.hasDupReceipts).length,
            txns: bills.reduce((n, b) => n + b.txns.length, 0), total: bills.reduce((n, b) => n + b.txnTotal, 0),
        };
    }, [pep]);

    const q = search.trim().toLowerCase();
    const stayMatches = (s) => !q || [s.stay_id, s.confirmation_num, s.lastname, s.firstname, s.email, s.rate_plan_id, s.group_code]
        .some((v) => String(v).toLowerCase().includes(q));
    const billRows = (pred) => (pep ? pep.stays.filter(stayMatches).flatMap((s) => s.bills.filter((b) => pred(b, s)).map((b) => ({ s, b }))) : []);
    const toggle = (key) => setDeleted((d) => { const n = new Set(d); if (n.has(key)) n.delete(key); else n.add(key); return n; });

    const saveClean = async () => {
        const xml = await exportCleanPepXml(raw, deleted);
        downloadBlob(new Blob([xml], { type: 'application/xml' }), `${fileName.replace(/\.[^.]+$/, '')}_cleaned.xml`);
    };

    const BillTable = ({ rows }) => (
        <div className="otx-table-wrap">
            <table className="otx-table">
                <thead><tr><th>Stay</th><th>Conf</th><th>Guest</th><th>Arrival</th><th>Receipt</th><th>MOP / card</th><th>Token / card id</th><th>Expiry</th><th>Txns</th><th>Net</th><th>Remove</th></tr></thead>
                <tbody>
                    {rows.map(({ s, b }) => (
                        <tr key={b.key} className={deleted.has(b.key) ? 'tkb-row-deleted' : undefined}>
                            <td>{s.stay_id}</td><td>{s.confirmation_num}</td><td>{s.lastname}, {s.firstname}</td><td>{s.arrival_date}</td>
                            <td>{b.receipt_id}{s.hasDupReceipts && <span className="tkb-badge tkb-cmp-partial" style={{ marginLeft: 4 }}>dup</span>}</td>
                            <td>{b.mop_type} {b.cc_type}</td>
                            <td>{b.cc_id}{b.isHTS && <span className="tkb-badge tkb-cmp-cloud-only" style={{ marginLeft: 4 }}>HTS</span>}</td>
                            <td className={b.isFakeExp ? 'otx-fail' : undefined}>{b.cc_exp || '—'}</td>
                            <td>{b.txns.length}</td><td className="otx-numeric">{money(b.txnTotal)}</td>
                            <td><input type="checkbox" checked={deleted.has(b.key)} onChange={() => toggle(b.key)} title="Remove this bill from the cleaned file" /></td>
                        </tr>
                    ))}
                    {!rows.length && <tr><td colSpan={11} className="otx-empty">Nothing here.</td></tr>}
                </tbody>
            </table>
        </div>
    );

    const glResult = useMemo(() => {
        if (!gl || !pep) return null;
        const { matched, glOnly, pepOnly } = matchGlToPep(gl, pep.stays);
        const rows = [
            ...matched.map(({ gl: g, stay }) => ({ status: 'match', statusText: 'Matched', room: g.roomNo, operaName: g.nameRaw,
                pepName: `${stay.lastname}, ${stay.firstname}`, arrival: g.arrDate, departure: g.depDate, balance: money(g.balance), stayId: stay.stay_id })),
            ...glOnly.map((g) => ({ status: 'missing', statusText: 'In Opera, not in PEP', room: g.roomNo, operaName: g.nameRaw,
                pepName: '', arrival: g.arrDate, departure: g.depDate, balance: money(g.balance), stayId: '' })),
            ...pepOnly.map((s) => ({ status: 'cloud-only', statusText: 'In PEP, not in Opera', room: '', operaName: '',
                pepName: `${s.lastname}, ${s.firstname}`, arrival: s.arrival_date, departure: s.departure_date, balance: '', stayId: s.stay_id })),
        ];
        return { rows, summary: [
            { label: 'Guest ledger rows', value: gl.length }, { label: 'Matched', value: matched.length, tone: 'ok' },
            { label: 'In Opera, not in PEP', value: glOnly.length, tone: glOnly.length ? 'warn' : 'ok' },
            { label: 'In PEP, not in Opera', value: pepOnly.length, tone: pepOnly.length ? 'warn' : 'ok' },
            { label: 'Ledger balance', value: money(gl.reduce((n, r) => n + r.balance, 0)) },
        ] };
    }, [gl, pep]);

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="PEP Guest Balancing"
                subtitle="Check a PEP guest file before import — HTS tokens, placeholder expiries, duplicate receipts, transactions — and clean it." />

            <ToolkitLocalFileStep number={1} title="PEP guest file" label="Choose the PEP guest XML" accept=".xml,.txt"
                onFile={async (text, file) => {
                    const parsed = await parsePepGuestXml(text);
                    setRaw(text); setFileName(file.name); setPep(parsed); setDeleted(new Set());
                    return `${parsed.stays.length} stay(s) · ${parsed.hotelInfo.hotelCode} · business date ${parsed.hotelInfo.businessDate}`;
                }} />
            <ToolkitLocalFileStep number={2} title="Opera guest ledger (optional)" label="Choose naGuestLedgerDetail PDF" accept=".pdf" asBuffer
                onFile={async (buf) => { const rows = await parseGuestLedgerPdf(buf); setGl(rows); return `${rows.length} guest ledger row(s)`; }} />

            {stats && (
                <>
                    <div className="otx-stats">
                        <div className="otx-stat"><span className="otx-stat-label">Stays</span><span className="otx-stat-value">{stats.stays}</span></div>
                        <div className="otx-stat"><span className="otx-stat-label">Folio bills</span><span className="otx-stat-value">{stats.bills}</span></div>
                        <div className="otx-stat"><span className="otx-stat-label">HTS tokens</span><span className="otx-stat-value tkb-warn-text">{stats.hts}</span></div>
                        <div className="otx-stat"><span className="otx-stat-label">2099 expiry</span><span className="otx-stat-value otx-fail">{stats.fake}</span></div>
                        <div className="otx-stat"><span className="otx-stat-label">Stays with duplicate receipts</span><span className="otx-stat-value otx-fail">{stats.dupStays}</span></div>
                        <div className="otx-stat"><span className="otx-stat-label">Transactions</span><span className="otx-stat-value">{stats.txns} · {money(stats.total)}</span></div>
                    </div>

                    <div className="tkb-toolbar">
                        <div className="tkb-tabs" style={{ margin: 0 }}>
                            {TABS.map(([k, label]) => (
                                <button key={k} type="button" className={`tkb-tab${tab === k ? ' tkb-tab-active' : ''}`} onClick={() => setTab(k)} disabled={k === 'gl' && !gl}>{label}</button>
                            ))}
                        </div>
                        <input className="tkb-search" type="search" placeholder="Search stays…" value={search} onChange={(e) => setSearch(e.target.value)} />
                        <div className="tkb-toolbar-spacer" />
                        {stats.dupStays > 0 && <Button variant="secondary" onClick={() => setDeleted(duplicateBillKeys(pep.stays))}><Copy className="icon" /> Mark all duplicates</Button>}
                        {deleted.size > 0 && <Button variant="secondary" onClick={() => setDeleted(new Set())}><RotateCcw className="icon" /> Clear marks</Button>}
                        <Button variant="success" onClick={saveClean} disabled={!deleted.size}><Download className="icon" /> Save cleaned XML ({deleted.size} removed)</Button>
                    </div>

                    {tab === 'hts' && <BillTable rows={billRows((b) => b.isHTS)} />}
                    {tab === 'fake' && <BillTable rows={billRows((b) => b.isFakeExp)} />}
                    {tab === 'dups' && <BillTable rows={billRows((_, s) => s.hasDupReceipts)} />}
                    {tab === 'txns' && (
                        <div className="otx-table-wrap">
                            <table className="otx-table">
                                <thead><tr><th>Accounting id</th><th>Description</th><th>Count</th><th>Charges</th><th>Payments</th><th>Net</th></tr></thead>
                                <tbody>
                                    {transactionRollup(pep.stays.filter(stayMatches)).map((r) => (
                                        <tr key={r.id}><td>{r.id}</td><td>{r.desc}</td><td>{r.count}</td>
                                            <td className="otx-numeric">{money(r.charges)}</td><td className="otx-numeric">{money(r.payments)}</td><td className="otx-numeric">{money(r.total)}</td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {tab === 'gl' && glResult && (
                        <ToolkitCompareResults summary={glResult.summary} rows={glResult.rows} exportName="PEP_Guest_Ledger_Compare"
                            statuses={[{ value: 'match', label: 'Matched' }, { value: 'missing', label: 'In Opera, not in PEP' }, { value: 'cloud-only', label: 'In PEP, not in Opera' }]}
                            columns={[{ key: 'room', label: 'Room' }, { key: 'operaName', label: 'Opera name' }, { key: 'pepName', label: 'PEP name' },
                                { key: 'arrival', label: 'Arrival' }, { key: 'departure', label: 'Departure' }, { key: 'balance', label: 'Ledger balance' }, { key: 'stayId', label: 'PEP stay' }]}
                            note="Matched on last name + arrival + departure." />
                    )}
                </>
            )}
        </div>
    );
}

export default ToolkitPepGuestBalancingPage;
